import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { agreeToTerms, deleteMyAccount, fetchMe, setAuthTokenProvider } from '../api/client';
import type { ImageQuota, TermsStatus } from '../api/types';
import { ConsentDialog } from '../components/ConsentDialog';
import { SignInDialog, type EmailMode } from '../components/SignInDialog';
import { authEnabled, enabledProviders, passwordEnabled, socialProviders, type AuthProviderId } from './config';
import type { SignedInUser } from './firebase';
import { LineLoginError, startLineRedirect, takeLineRedirectResult } from './line';

/**
 * ログイン状態をアプリ全体で 1 つだけ持つ（doc/AUTH.md §5）。
 *
 * firebase SDK は初期バンドルに乗せない。読むのは次の 2 つのときだけ:
 *   1. ログインボタンを押したとき
 *   2. 前にログインしていた形跡（localStorage の印）があるとき
 * 診断だけを受けにきた人には SDK を読ませない。
 *
 * ID トークンは持ち回らず、API を呼ぶたびに SDK から取り直す（1 時間で切れるため）。
 * ログイン方法を選ぶダイアログもここが出す（`requestSignIn()` が「入れたか」を返す）。
 */
const SIGNED_IN_FLAG = 'gendan.signedIn';

export interface AuthValue {
  /** ログイン状態が確定したか（確定するまでヘッダーはボタンを出さない）。 */
  readonly ready: boolean;
  readonly user: SignedInUser | null;
  readonly isAdmin: boolean;
  /** どのソーシャルアカウントで入っているか（`google.com` など）。 */
  readonly provider: string | null;
  /** 今日あと何回 絵を描けるか。ログインしていなければ null。 */
  readonly quota: ImageQuota | null;
  /**
   * 利用規約・プライバシーポリシーへの同意（doc/AUTH.md §13）。ログインしていなければ null。
   * `current` が false なら、いまの版に同意していない（初回か、版が上がった）。
   */
  readonly terms: TermsStatus | null;
  /** VITE_FIREBASE_* が入っている環境か。無ければログインボタンは「準備中」。 */
  readonly enabled: boolean;
  readonly busy: boolean;
  /** メールアドレスの確認が済んでいるか。 */
  readonly emailVerified: boolean;
  readonly email: string | null;
  /** ログイン方法を選んでもらう。入れたら true。すでに入っていれば何もせず true。 */
  requestSignIn(): Promise<boolean>;
  signOut(): Promise<void>;
  /** 絵を描いたあとなど、残り回数を取り直す。 */
  refresh(): Promise<void>;
  /** 確認メールを送り直す。 */
  sendVerification(): Promise<void>;
  /** 確認リンクを開いたあとに、状態と ID トークンを取り直す。 */
  reloadUser(): Promise<void>;
  /** 表示名を変える。空なら消す。失敗したらそのまま投げる。 */
  changeDisplayName(name: string): Promise<void>;
  /** いまの版に同意した記録を残す（同意のダイアログから呼ぶ）。 */
  agree(): Promise<void>;
  /** 削除の前の本人確認をパスワードでするか（それ以外はポップアップで入り直す）。 */
  loginMethod(): Promise<AuthProviderId | null>;
  /** 本人確認のために入り直す。失敗したら画面に出せる文言で投げる。 */
  reauthenticate(password?: string): Promise<void>;
  /**
   * アカウントを削除する（doc/AUTH.md §18）。本人確認で入り直す → GenDan のデータを消す → Firebase のアカウントを消す → ログアウト。
   * 失敗したら画面に出せる文言で投げる。
   */
  deleteAccount(password?: string): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

const readFlag = (): boolean => {
  try { return window.localStorage.getItem(SIGNED_IN_FLAG) === '1'; } catch { return false; }
};
const writeFlag = (value: boolean): void => {
  try {
    if (value) window.localStorage.setItem(SIGNED_IN_FLAG, '1');
    else window.localStorage.removeItem(SIGNED_IN_FLAG);
  } catch { /* プライベートモードなどで書けなくても動く */ }
};

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [user, setUser] = useState<SignedInUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [provider, setProvider] = useState<string | null>(null);
  const [quota, setQuota] = useState<ImageQuota | null>(null);
  const [terms, setTerms] = useState<TermsStatus | null>(null);
  // 「あとで」を押した人に、同じセッションで何度も出さない
  const [consentDismissed, setConsentDismissed] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [consentBusy, setConsentBusy] = useState(false);
  // 設定が無い環境、または前にログインした形跡が無ければ、待たずに「確定」でよい
  const [ready, setReady] = useState(() => !authEnabled || !readFlag());
  const [picking, setPicking] = useState(false);
  const [busyProvider, setBusyProvider] = useState<AuthProviderId | null>(null);
  const [dialogError, setDialogError] = useState('');
  const [dialogNotice, setDialogNotice] = useState('');
  const unwatch = useRef<(() => void) | null>(null);
  // requestSignIn() の呼び手へ「入れたか」を返すための置き場
  const answer = useRef<((signedIn: boolean) => void) | null>(null);

  // API クライアントに「トークンが要るときはここへ」と教える
  useEffect(() => {
    setAuthTokenProvider(async () => {
      if (!authEnabled) return null;
      const { currentIdToken } = await import('./firebase');
      return currentIdToken().catch(() => null);
    });
  }, []);

  const startWatching = useCallback(async () => {
    if (!authEnabled || unwatch.current) return;
    const { watchAuth } = await import('./firebase');
    unwatch.current = watchAuth((next) => {
      setUser(next);
      setReady(true);
      writeFlag(Boolean(next));
      if (!next) { setIsAdmin(false); setProvider(null); setQuota(null); setTerms(null); }
    });
  }, []);

  // 前にログインしていた人だけ、SDK を読んで状態を戻す
  useEffect(() => {
    if (authEnabled && readFlag()) void startWatching().catch(() => setReady(true));
    return () => { unwatch.current?.(); unwatch.current = null; };
  }, [startWatching]);

  // LINE からページごと戻ってきたとき（ポップアップが開けなかった。doc/AUTH.md §21）。結果は 1 度だけ拾う
  useEffect(() => {
    if (!authEnabled) return;
    const outcome = takeLineRedirectResult();
    if (!outcome) return;
    if (outcome instanceof LineLoginError) {
      setDialogError(outcome.message);
      setPicking(true);
      return;
    }
    setReady(false);
    void (async () => {
      const { signInWithLineGrant } = await import('./firebase');
      await startWatching();
      await signInWithLineGrant(outcome);
      writeFlag(true);
    })()
      .catch(() => { setDialogError('LINE でのログインに失敗しました。もう一度お試しください。'); setPicking(true); })
      .finally(() => setReady(true));
  }, [startWatching]);

  // ログインが分かったら、権限と残り回数を聞きに行く
  const refresh = useCallback(async () => {
    if (!user) { setIsAdmin(false); setProvider(null); setQuota(null); setTerms(null); return; }
    try {
      const value = await fetchMe();
      setIsAdmin(value.isAdmin);
      setProvider(value.provider);
      setQuota(value.imageQuota);
      setTerms(value.terms ?? null);
    } catch {
      // 取れなくてもログインはできている（画面は残り回数を出さないだけ）
      setQuota(null);
    }
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  // 管理者のアクセスは Google Analytics に送らない（services/api の lib/analytics.ts）。一度止めたら、そのページを開いているあいだは止めたまま
  useEffect(() => {
    const id = window.__GENDAN_CONFIG__?.analytics?.measurementId;
    if (id && isAdmin) (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = true;
  }, [isAdmin]);

  const settle = useCallback((signedIn: boolean) => {
    answer.current?.(signedIn);
    answer.current = null;
    setPicking(false);
    setBusyProvider(null);
  }, []);

  // 選んでいる最中にログインが成り立ったら閉じる（LINE の窓が閉じたように見えたあとで結果が届いたときなど）
  useEffect(() => {
    if (picking && user) settle(true);
  }, [picking, user, settle]);

  const runSignIn = useCallback(async (id: AuthProviderId) => {
    setDialogError('');
    setDialogNotice('');
    setBusyProvider(id);
    try {
      const { signInWithProvider } = await import('./firebase');
      await startWatching();
      const { user: next, linked } = await signInWithProvider(id);
      setUser((current) => current ?? next);
      writeFlag(true);
      setReady(true);
      if (linked) setDialogNotice('前から使っていたアカウントに繋ぎました。次からはどちらでも入れます。');
      settle(true);
    } catch (cause) {
      // LINE のポップアップが開けない（アプリ内ブラウザなど）ときは、ページごと LINE へ移る。戻ると上の useEffect が拾う
      if (cause instanceof LineLoginError && cause.code === 'popup-blocked') {
        startLineRedirect();
        return;
      }
      const { signInErrorMessage } = await import('./firebase');
      setDialogError(signInErrorMessage(cause, id));
      setBusyProvider(null);
      // ダイアログは開けたままにして、別の方法を選べるようにする
      if (!picking) { setPicking(true); }
    }
  }, [picking, settle, startWatching]);

  /** メールアドレスでのログイン・新規登録・パスワード再設定。 */
  const runEmail = useCallback(async (mode: EmailMode, input: { email: string; password: string; displayName: string }) => {
    setDialogError('');
    setDialogNotice('');
    setBusyProvider('password');
    try {
      const mod = await import('./firebase');
      await startWatching();
      if (mode === 'reset') {
        await mod.sendPasswordReset(input.email);
        // 登録の有無を教えないため、どちらでも同じ言い方にする
        setDialogNotice('再設定のメールを送りました。届いていないときは、迷惑メールも見てください。');
        setBusyProvider(null);
        return;
      }
      const next = mode === 'signup'
        ? await mod.signUpWithEmail(input.email, input.password, input.displayName)
        : await mod.signInWithEmail(input.email, input.password);
      setUser((current) => current ?? next);
      writeFlag(true);
      setReady(true);
      if (mode === 'signup') setDialogNotice('確認のメールを送りました。届いたメールのリンクを開いてください。');
      settle(true);
    } catch (cause) {
      const { signInErrorMessage } = await import('./firebase');
      setDialogError(signInErrorMessage(cause, 'password'));
      setBusyProvider(null);
    }
  }, [settle, startWatching]);

  /** 同意を記録して、その場の状態も新しくする（doc/AUTH.md §13）。 */
  const agree = useCallback(async () => {
    const { terms: next } = await agreeToTerms();
    setTerms(next);
  }, []);

  const sendVerification = useCallback(async () => {
    const { resendEmailVerification } = await import('./firebase');
    await resendEmailVerification();
  }, []);

  const reloadUser = useCallback(async () => {
    const { refreshUser } = await import('./firebase');
    const next = await refreshUser();
    if (next) setUser(next);
    await refresh();
  }, [refresh]);

  const changeDisplayName = useCallback(async (name: string) => {
    const { updateDisplayName } = await import('./firebase');
    setUser(await updateDisplayName(name));
  }, []);

  const requestSignIn = useCallback(async (): Promise<boolean> => {
    if (user) return true;
    if (!authEnabled) return false;
    // 選べる方法がソーシャル 1 つだけ（メールも無い）なら、選ばせずにそのまま入る
    if (enabledProviders.length === 1 && !passwordEnabled && socialProviders.length === 1) {
      const done = new Promise<boolean>((resolve) => { answer.current = resolve; });
      void runSignIn(enabledProviders[0]);
      return done;
    }
    setDialogError('');
    setDialogNotice('');
    setPicking(true);
    return new Promise<boolean>((resolve) => { answer.current = resolve; });
  }, [runSignIn, user]);

  const signOutNow = useCallback(async () => {
    setBusyProvider('google');
    try {
      const { signOutFirebase } = await import('./firebase');
      await signOutFirebase();
    } catch { /* すでに切れていてもよい */ } finally {
      writeFlag(false);
      setUser(null);
      setIsAdmin(false);
      setProvider(null);
      setQuota(null);
      setTerms(null);
      setConsentDismissed(false);
      setBusyProvider(null);
    }
  }, []);

  const loginMethod = useCallback(async () => {
    const { currentLoginMethod } = await import('./firebase');
    return currentLoginMethod();
  }, []);

  const reauthNow = useCallback(async (password?: string): Promise<void> => {
    const mod = await import('./firebase');
    try {
      await mod.reauthenticate(password);
    } catch (cause) {
      throw new Error(mod.reauthErrorMessage(cause));
    }
  }, []);

  const deleteAccountNow = useCallback(async (password?: string): Promise<void> => {
    await reauthNow(password);
    const mod = await import('./firebase');
    await deleteMyAccount();
    // GenDan のデータはもう消えている。Firebase 側で失敗しても（まれ）ログアウトはする。次に入れば空の利用者として始まる
    await mod.deleteFirebaseUser().catch(() => undefined);
    await signOutNow();
  }, [reauthNow, signOutNow]);

  const value = useMemo<AuthValue>(
    () => ({
      ready, user, isAdmin, provider, quota, terms, enabled: authEnabled,
      busy: busyProvider !== null,
      emailVerified: user?.emailVerified ?? false,
      email: user?.email ?? null,
      requestSignIn, signOut: signOutNow, refresh, sendVerification, reloadUser, changeDisplayName, agree,
      loginMethod, reauthenticate: reauthNow, deleteAccount: deleteAccountNow,
    }),
    [ready, user, isAdmin, provider, quota, terms, busyProvider, requestSignIn, signOutNow, refresh, sendVerification, reloadUser, changeDisplayName, agree, loginMethod, reauthNow, deleteAccountNow],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* 同意の確認（初回ログインと、版が上がったとき）。ログイン方法を選んでいる最中には出さない */}
      {!picking && user && terms && !terms.current && !consentDismissed && (
        <ConsentDialog
          renewal={Boolean(terms.agreed)}
          busy={consentBusy}
          error={consentError}
          onAgree={() => {
            setConsentBusy(true);
            setConsentError('');
            void agree()
              .catch(() => setConsentError('記録できませんでした。通信を確かめて、もう一度お試しください。'))
              .finally(() => setConsentBusy(false));
          }}
          onLater={() => setConsentDismissed(true)}
        />
      )}
      {picking && (
        <SignInDialog
          busy={busyProvider !== null}
          error={dialogError}
          notice={dialogNotice}
          onPickSocial={(id) => void runSignIn(id)}
          onEmail={(mode, input) => void runEmail(mode, input)}
          onClose={() => settle(false)}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider の外で useAuth を呼んでいます。');
  return value;
}
